import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Divider,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  Security as SecurityIcon,
  BugReport as BugReportIcon,
  Assignment as AssignmentIcon,
  Build as BuildIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { get, authPost } from '../../services/apiService';
import { isAuthenticated } from '../../services/authService';
import SnykIssuesTable from './SnykIssuesTable';
import SnykSummaryCards from './SnykSummaryCards';
import SnykProjectTargetTable from './SnykProjectTargetTable';
import SnykTeamTargetTable from './SnykTeamTargetTable';
import SnykPackageChart from './SnykPackageChart';
import SnykWeeklySlaChart from './SnykWeeklySlaChart';

const SnykDashboard = ({ collection = '' }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showUnresolved, setShowUnresolved] = useState(true);
  const [outOfSLA, setOutOfSLA] = useState(false);

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncAlreadyRunning, setSyncAlreadyRunning] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const syncPollRef = useRef(null);

  const [filters, setFilters] = useState({
    severity: 'Critical,High,Medium',
    state: 'Open'
  });

  const fetchSyncStatus = useCallback(async () => {
    try {
      const res = await get('/api/snyk/sync-status');
      if (res?.success) {
        setSyncAlreadyRunning(res.isSyncing);
        if (res.lastSync) setLastSync(new Date(res.lastSync));
      }
    } catch (_) {}
  }, []);

  // Poll sync status while a sync is in progress (stops once done)
  useEffect(() => {
    if (syncing) {
      syncPollRef.current = setInterval(fetchSyncStatus, 5000);
    } else {
      clearInterval(syncPollRef.current);
    }
    return () => clearInterval(syncPollRef.current);
  }, [syncing, fetchSyncStatus]);

  // Fetch initial sync status on mount
  useEffect(() => { fetchSyncStatus(); }, [fetchSyncStatus]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      if (outOfSLA) queryParams.append('outOfSLA', 'true');
      else if (showUnresolved) queryParams.append('showUnresolved', 'true');
      if (collection) queryParams.append('projectCollection', collection);

      const queryString = queryParams.toString();
      const url = `/api/snyk/dashboard${queryString ? `?${queryString}` : ''}`;
      const response = await get(url);
      if (response) {
        setDashboardData(response);
        if (response.lastUpdated) setLastSync(new Date(response.lastUpdated));
      } else {
        throw new Error('No data received from server');
      }
    } catch (err) {
      console.error('Error fetching SNYK dashboard data:', err);
      setError(err.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, [showUnresolved, outOfSLA, collection]);

  const prevRefreshTriggerRef = useRef(refreshTrigger);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    if (prevRefreshTriggerRef.current !== refreshTrigger && prevRefreshTriggerRef.current !== 0) {
      fetchDashboardData();
    }
    prevRefreshTriggerRef.current = refreshTrigger;
  }, [refreshTrigger]);

  const handleManualRefresh = async () => {
    if (syncing || syncAlreadyRunning) return;
    setSyncing(true);
    setSyncMessage(null);
    try {
      const res = await authPost('/api/snyk/refresh', {});
      if (res?.success) {
        setSyncMessage({ type: 'success', text: `Sync complete — ${res.summary?.totalIssues ?? '?'} issues loaded.` });
        await fetchDashboardData();
        await fetchSyncStatus();
      } else {
        setSyncMessage({ type: 'error', text: res?.message || 'Sync failed.' });
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        setSyncAlreadyRunning(true);
        setSyncMessage({ type: 'warning', text: 'Sync is already running. Please wait.' });
      } else {
        setSyncMessage({ type: 'error', text: e?.response?.data?.message || e.message || 'Sync failed.' });
      }
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={fetchDashboardData}>Retry</Button>
      </Box>
    );
  }

  if (!dashboardData?.hasData) {
    return (
      <Box p={3}>
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <SecurityIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h4" gutterBottom>SNYK Security Dashboard</Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            No SNYK vulnerability data available. Please sync SNYK issues from the Admin panel to get started.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const snykBaseUrl = 'https://app.snyk.io/org/206259_highqus/reporting?v=1&context[page]=issues-detail&issue_by=Severity&table_issues_detail_cols=SEVERITY%257CSCORE%257CCVE%257CCWE%257CPROJECT%257CEXPLOIT%2520MATURITY%257CCOMPUTED%2520FIXABILITY%257CINTRODUCED%257CSNYK%2520PRODUCT%257CCODE%2520REGION%257CCOMMIT%2520ID%257CFILE%2520PATH%257CINTRODUCTION%2520CATEGORY%257CISSUE%2520STATUS%257CFIXED%2520IN%2520AVAILABLE%257CFIXED%2520IN%2520VERSION%257CHAS%2520JIRA%2520ISSUE(S)%2520ASSIGNED%257CLATEST%2520JIRA%2520ISSUE%257CJIRA%2520ISSUES%2520LIST%257CLAST%2520IGNORED%257CLAST%2520INTRODUCED%257CLAST%2520RESOLVED%257CATTACK%2520VECTOR%257CSNYK%2520CVSS%2520SCORE%257CNVD%2520SCORE%257CNVD%2520SEVERITY%257CEPSS%2520SCORE%257CEPSS%2520PERCENTILE%257CORG%2520NAME%257CPACKAGE%2520NAME%2520AND%2520VERSION%257CPROBLEM%2520ID%257CPROJECT%2520COLLECTION%257CPROJECT%2520CRITICALITY%257CPROJECT%2520ENVIRONMENT%257CPROJECT%2520LIFECYCLE%257CPROJECT%2520ORIGIN%257CPROJECT%2520OWNER%257CPROJECT%2520TAGS%257CPROJECT%2520TARGET%257CPROJECT%2520TARGET%2520REFERENCE%257CPROJECT%2520TYPE%257CREACHABILITY%257CSEMVER%2520VULNERABLE%2520RANGE%257CVULNERABILITY%2520PUBLICATION%2520DATE%257CASSET%2520NAME%257CASSET%2520ID%257CPARENT%2520ASSET%2520NAME%257CPARENT%2520ASSET%2520ID%257CASSET%2520CLASS%257CASSET%2520TYPE%257CASSET%2520TAGS%257CREPOSITORY%2520FRESHNESS%257CASSET%2520APPLICATION%257CASSET%2520OWNER%257CASSET%2520CATEGORY%257CASSET%2520CATALOG%2520NAME%257CASSET%2520FINDING%2520ID%257CASSET%2520LIFECYCLE&tableRowsPerPage=10&project_origin=%255B%2522github-cloud-app%2522%255D&lifecycle=%257B%2522Lifecycle%2522%253A%255B%2522Production%2522%255D%257D&fromGitHubAuth=true&fromGitHubAuth=true&fromGitHubAuth=true&fromGitHubAuth=true&fromGitHubAuth=true&fromGitHubAuth=true&fromGitHubAuth=true&fromGitHubAuth=true&table_issues_detail_sort=SCORE%2520DESC&product_name=%255B%2522Snyk%2520Code%2522%252C%2522Snyk%2520Open%2520Source%2522%255D&issue_status=%255B%2522Open%2522%255D&table_issues_detail_rows=10';
  const snykCollectionBaseUrl = 'https://app.snyk.io/org/206259_highqus/analytics?v=1&context[page]=issues-detail&issue_by=Severity&table_issues_detail_cols=SEVERITY%257CSCORE%257CCVE%257CCWE%257CPROJECT%257CEXPLOIT%2520MATURITY%257CCOMPUTED%2520FIXABILITY%257CINTRODUCED%257CSNYK%2520PRODUCT%257CCODE%2520REGION%257CCOMMIT%2520ID%257CFILE%2520PATH%257CINTRODUCTION%2520CATEGORY%257CISSUE%2520STATUS%257CFIXED%2520IN%2520AVAILABLE%257CFIXED%2520IN%2520VERSION%257CHAS%2520JIRA%2520ISSUE(S)%2520ASSIGNED%257CLATEST%2520JIRA%2520ISSUE%257CJIRA%2520ISSUES%2520LIST%257CLAST%2520IGNORED%257CLAST%2520INTRODUCED%257CLAST%2520RESOLVED%257CATTACK%2520VECTOR%257CSNYK%2520CVSS%2520SCORE%257CNVD%2520SCORE%257CNVD%2520SEVERITY%257CEPSS%2520SCORE%257CEPSS%2520PERCENTILE%257CORG%2520NAME%257CPACKAGE%2520NAME%2520AND%2520VERSION%257CPROBLEM%2520ID%257CPROJECT%2520COLLECTION%257CPROJECT%2520CRITICALITY%257CPROJECT%2520ENVIRONMENT%257CPROJECT%2520LIFECYCLE%257CPROJECT%2520ORIGIN%257CPROJECT%2520OWNER%257CPROJECT%2520TAGS%257CPROJECT%2520TARGET%257CPROJECT%2520TARGET%2520REFERENCE%257CPROJECT%2520TYPE%257CREACHABILITY%257CSEMVER%2520VULNERABLE%2520RANGE%257CVULNERABILITY%2520PUBLICATION%2520DATE%257CASSET%2520NAME%257CASSET%2520ID%257CPARENT%2520ASSET%2520NAME%257CPARENT%2520ASSET%2520ID%257CASSET%2520CLASS%257CASSET%2520TYPE%257CASSET%2520TAGS%257CREPOSITORY%2520FRESHNESS%257CASSET%2520APPLICATION%257CASSET%2520OWNER%257CASSET%2520CATEGORY%257CASSET%2520CATALOG%2520NAME%257CASSET%2520FINDING%2520ID%257CASSET%2520LIFECYCLE&tableRowsPerPage=10&project_origin=%255B%2522github-cloud-app%2522%255D&fromGitHubAuth=true&fromGitHubAuth=true&fromGitHubAuth=true&fromGitHubAuth=true&fromGitHubAuth=true&fromGitHubAuth=true&fromGitHubAuth=true&fromGitHubAuth=true&table_issues_detail_sort=SCORE%2520DESC&product_name=%255B%2522Snyk%2520Code%2522%252C%2522Snyk%2520Open%2520Source%2522%255D&issue_status=%255B%2522Open%2522%255D&table_issues_detail_rows=10';
  const snykUrl = collection
    ? `${snykCollectionBaseUrl}&project_collection=%255B%2522${encodeURIComponent(collection).replace(/%20/g, '%2520')}%2522%255D&project_collections=%257B%2522collection_name%2522%253A%255B%2522${encodeURIComponent(collection).replace(/%20/g, '%2520')}%2522%255D%257D`
    : snykBaseUrl;

  const isSyncBusy = syncing || syncAlreadyRunning;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h4" gutterBottom>
              <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              SNYK Security Dashboard
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNewIcon />}
              href={snykUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mb: 1 }}
            >
              View in Snyk
            </Button>
          </Box>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              Last synced: <strong>{formatDate(lastSync)}</strong>
            </Typography>
            {isSyncBusy && (
              <Chip
                icon={<SyncIcon sx={{ animation: 'spin 1.2s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />}
                label={syncAlreadyRunning && !syncing ? 'Sync running…' : 'Syncing…'}
                size="small"
                color="info"
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={2} mt={1} flexWrap="wrap">
          <FormControlLabel
            control={<Checkbox checked={showUnresolved} onChange={(e) => setShowUnresolved(e.target.checked)} color="primary" />}
            label="Show Unresolved"
          />
          <FormControlLabel
            control={<Checkbox checked={outOfSLA} onChange={(e) => setOutOfSLA(e.target.checked)} color="error" />}
            label="Out of SLA"
          />
          {isAuthenticated() && (
            <Tooltip title={isSyncBusy ? 'Sync already in progress' : 'Pull latest data from Snyk'}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={syncing ? <CircularProgress size={14} /> : <RefreshIcon />}
                  onClick={handleManualRefresh}
                  disabled={isSyncBusy}
                >
                  {syncing ? 'Syncing…' : syncAlreadyRunning ? 'Sync running…' : 'Refresh Data'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {syncMessage && (
        <Alert
          severity={syncMessage.type}
          onClose={() => setSyncMessage(null)}
          sx={{ mb: 2 }}
        >
          {syncMessage.text}
        </Alert>
      )}

      <SnykSummaryCards data={dashboardData.summary} />
      <SnykProjectTargetTable showUnresolved={showUnresolved} outOfSLA={outOfSLA} collection={collection} />
      <SnykTeamTargetTable showUnresolved={showUnresolved} outOfSLA={outOfSLA} collection={collection} />
      <SnykWeeklySlaChart showUnresolved={showUnresolved} outOfSLA={outOfSLA} collection={collection} />
      <SnykPackageChart showUnresolved={showUnresolved} outOfSLA={outOfSLA} collection={collection} />

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              <BugReportIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Security Issues
            </Typography>
            <Chip label="Critical, High & Medium (Default Filter)" color="warning" size="small" />
          </Box>
          <Divider sx={{ mb: 2 }} />
          <SnykIssuesTable
            onDataChange={() => setRefreshTrigger(prev => prev + 1)}
            filters={{ ...filters, outOfSLA }}
            collection={collection}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default SnykDashboard;
